import { Button, Heading, Separator, Text, TextField } from '@praetoid/ui';
import { useForm } from '@tanstack/react-form';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { AxiosError } from 'axios';

import { getUserQueryOptions } from '@praetoid/client';

import { useAuth } from '~/context/useAuth';
import { getCurrentUser, putCurrentUser } from '~/lib/currentUser';
import { parseError, parseFieldError } from '~/lib/errors';
import { validateSigninField } from '~/routes/_auth/-validation';

export const Route = createFileRoute('/_auth/signin')({
	beforeLoad: async ({ context }) => {
		if (getCurrentUser()) throw redirect({ to: '/home' });

		try {
			const user = await context.queryClient.ensureQueryData(getUserQueryOptions());
			putCurrentUser(user);
			throw redirect({ to: '/home' });
		} catch (error) {
			if (error instanceof AxiosError && error.response?.status === 401) return;
			throw error;
		}
	},
	component: RouteComponent
});

function RouteComponent() {
	const { signinMutation } = useAuth();
	const { mutateAsync: signIn, isPending, error } = signinMutation;

	const form = useForm({
		defaultValues: {
			identifier: 'test@test.com',
			password: 'Password123!'
		},
		onSubmit: async ({ value }) => {
			await signIn({ data: value });
		}
	});

	const generalError = parseError(error);

	return (
		<div className="ui-gradient grid h-screen grid-cols-[1fr_440px_1fr] grid-rows-1 p-4">
			<div className="flex h-full items-center justify-center" style={{ gridColumnStart: 2 }}>
				<div className="bg-panel flex w-full flex-col gap-9 rounded-md border p-12">
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
					>
						<div className="flex flex-col gap-4">
							<Heading>Sign in to your account</Heading>

							{generalError && (
								<div className="bg-redA-2 text-red-11 rounded-md p-3 text-sm">{generalError}</div>
							)}

							<form.Field
								name="identifier"
								validators={{
									onChange: ({ value }) => validateSigninField('identifier', value)
								}}
							>
								{(field) => {
									const serverError = parseFieldError(error, 'identifier');
									const displayError = field.state.meta.errors[0] || serverError;

									return (
										<div className="flex flex-col gap-2">
											<Text size="2" weight="medium">
												Email
											</Text>
											<TextField.Root
												placeholder="Your email address"
												size="2"
												name={field.name}
												value={field.state.value}
												onBlur={field.handleBlur}
												onChange={(e) => field.handleChange(e.target.value)}
												className={displayError ? 'border-red-500' : ''}
											/>
											{displayError && (
												<Text size="1" className="text-red-500">
													{displayError}
												</Text>
											)}
										</div>
									);
								}}
							</form.Field>

							<form.Field
								name="password"
								validators={{
									onChange: ({ value }) => validateSigninField('password', value)
								}}
							>
								{(field) => {
									const serverError = parseFieldError(error, 'password');
									const displayError = field.state.meta.errors[0] || serverError;

									return (
										<div className="flex flex-col gap-2">
											<Text size="2" weight="medium">
												Password
											</Text>
											<TextField.Root
												placeholder="Your password"
												size="2"
												type="password"
												name={field.name}
												value={field.state.value}
												onBlur={field.handleBlur}
												onChange={(e) => field.handleChange(e.target.value)}
												className={displayError ? 'border-red-500' : ''}
											/>
											{displayError && (
												<Text size="1" className="text-red-500">
													{displayError}
												</Text>
											)}
										</div>
									);
								}}
							</form.Field>

							<form.Subscribe selector={(state) => state.canSubmit}>
								{(canSubmit) => (
									<Button type="submit" disabled={!canSubmit || isPending}>
										{isPending ? 'Signing in...' : 'Continue'}
									</Button>
								)}
							</form.Subscribe>
						</div>
					</form>

					<div className="flex flex-col gap-4">
						<div className="flex items-center gap-2">
							<Separator className="flex-1" />
							<Text size="1" weight="medium" className="text-foreground-secondary uppercase">
								Or
							</Text>
							<Separator className="flex-1" />
						</div>

						<Button variant="outline">Continue with Google</Button>
					</div>

					<div className="inline-flex justify-center gap-2">
						<Text size="2" weight="medium" as="p" className="text-foreground-secondary">
							Don&apos;t have an account?{' '}
							<Text color="gold" className="hover:underline" asChild>
								<Link to="/signup">Get started</Link>
							</Text>
						</Text>
					</div>
				</div>
			</div>
		</div>
	);
}
